/**
 * "Download payslip" on the employee My Pay screen — the logic half, kept
 * out of `my_pay_screen.tsx` (already well over the 150-line component rule).
 *
 * Flow: `personalPayroll.generateMyPayslipPdf` renders the employee's own
 * payslip snapshot as a PDF → it is saved to Downloads → on a phone, ONE
 * "Payslip downloaded" notice is raised: the OS notification whose tap opens
 * the file (the tap listener lives in `lib/native_file_open.ts`, registered
 * once in `main.tsx`), or — only when it was not handed to the OS (no
 * notification API, permission denied, schedule failed) — an in-app bell
 * entry instead (`data/device_notifications.ts`). A notification the OS
 * accepted but then suppressed (channel turned off, focus / Do Not Disturb
 * mode) is not detectable through the plugin, so it gets no bell entry. Raising both read as two notifications
 * for one tap (WagePro bug-bash 2026-10-15, item #4). In a browser the save
 * IS the download, so no notification.
 *
 * Never throws: every failure resolves to `{ ok: false, reason }` so the
 * screen only ever renders a translated message (same contract as
 * `lib/auth/oauth_pay_run_post_actions.ts`). Dependencies are injected so the
 * flow is testable without a Capacitor runtime or a live tRPC client.
 */
import { platform } from '@ledger/platform';
import type { PayPeriod } from '../../data/types.js';
import {
  record_device_notification,
  type NewDeviceNotification,
} from '../../data/device_notifications.js';
import { getTrpcClient } from '../../lib/trpc.js';
import { isAal2Required } from '../../lib/auth/step_up.js';
import {
  decode_base64_to_blob,
  map_wagepro_locale_to_payslip_locale,
} from '../payroll/post_run_actions_calculations.js';

const PDF_MIME = 'application/pdf';

/**
 * Lifetime of the in-app "Payslip saved" confirmation: an explicit, short
 * value (the toast library defaults to 4000ms) so the intent is stated at
 * the call site. This is NOT what fixed the toast lingering over the tab bar
 * (bug-bash 2026-10-15 #3): that was the app-root toaster offset
 * (`components/layout/toaster_offset.ts`), which moves the stack clear of
 * the ESS tab bar so taps on a tab no longer land on the toast and pause
 * its hover timer. The OS notification and the in-app bell keep the durable
 * record of the download.
 */
export const PAYSLIP_SAVED_TOAST_DURATION_MS = 3500;

export type MyPayDownloadFailure = 'step_up' | 'error';

export type MyPayDownloadResult =
  | {
      ok: true;
      /**
       * True when the app saved the file itself (native shell) — the screen
       * then confirms in-app, because the notification may be suppressed
       * (permission denied) and there is no browser download shelf. False in
       * a browser, which shows its own download UI.
       */
      saved_on_device: boolean;
    }
  | { ok: false; reason: MyPayDownloadFailure };

export interface MyPayDownloadInput {
  entity_id: string;
  pay_run_id: string;
  /** The active WagePro UI locale (`en-001` / `si-LK` / `ta-LK`). */
  locale: string;
  /** The payslip's pay period — labels the in-app bell entry. */
  period: PayPeriod;
}

export interface MyPayDownloadLabels {
  /** Notification title, e.g. "Payslip downloaded". */
  title: string;
  /**
   * Notification body, e.g. "Tap to open". Deliberately generic: the
   * filename carries the employee's name and pay period, and a notification
   * body shows on the lock screen.
   */
  body: string;
}

export interface MyPayDownloadDeps {
  generate(input: {
    entity_id: string;
    pay_run_id: string;
    locale: 'en' | 'si' | 'ta';
  }): Promise<{ pdf_base64: string; filename: string }>;
  save(
    blob: Blob,
    filename: string,
    mime_type: string,
  ): Promise<{ uri: string | null }>;
  /** Resolves true when the OS actually showed the notification. */
  notify(payload: {
    title: string;
    body: string;
    href: string;
    mimeType: string;
  }): Promise<boolean>;
  is_native(): boolean;
  /**
   * Add the "payslip downloaded" entry to the in-app bell — the fallback
   * used only when the OS notification was not handed to the OS (no API,
   * permission denied, schedule failed).
   */
  record(entry: NewDeviceNotification): void;
}

function default_deps(): MyPayDownloadDeps {
  return {
    generate: (input) =>
      getTrpcClient().personalPayroll.generateMyPayslipPdf.query(input),
    save: (blob, filename, mime_type) =>
      platform.saveToDownloads(blob, filename, mime_type),
    notify: (payload) => platform.notify(payload),
    is_native: () => platform.isNative(),
    record: (entry) => {
      record_device_notification(entry);
    },
  };
}

export async function download_my_payslip(
  input: MyPayDownloadInput,
  labels: MyPayDownloadLabels,
  deps: MyPayDownloadDeps = default_deps(),
): Promise<MyPayDownloadResult> {
  let saved: { uri: string | null };
  try {
    const result = await deps.generate({
      entity_id: input.entity_id,
      pay_run_id: input.pay_run_id,
      locale: map_wagepro_locale_to_payslip_locale(input.locale),
    });
    // atob throws on a malformed payload — caught below like any other
    // failure rather than escaping as an unhandled rejection.
    saved = await deps.save(
      decode_base64_to_blob(result.pdf_base64, PDF_MIME),
      result.filename,
      PDF_MIME,
    );
  } catch (err) {
    if (isAal2Required(err)) return { ok: false, reason: 'step_up' };
    console.error('[wagepro-web] my pay payslip download failed', err);
    return { ok: false, reason: 'error' };
  }

  // The file is saved at this point, so a notification failure (permission
  // denied, plugin error) must not make the download look broken.
  const uri = deps.is_native() ? saved.uri : null;
  if (uri !== null) {
    // Exactly one notice per download. The OS notification is preferred (it
    // is what a phone user expects, and its tap opens the file); the bell
    // entry is only the fallback for when it was not handed to the OS (no
    // API, permission denied, schedule failed), so the download still leaves
    // a way back to the file. Suppression after the OS accepted it (channel
    // off, focus mode) is not detectable here.
    let shown = false;
    try {
      shown = await deps.notify({
        title: labels.title,
        body: labels.body,
        href: uri,
        mimeType: PDF_MIME,
      });
    } catch (err) {
      console.error(
        '[wagepro-web] payslip downloaded, but the notification failed',
        err,
      );
    }
    if (!shown) {
      try {
        deps.record({
          type: 'payslip_downloaded',
          entity_id: input.entity_id,
          uri,
          mime_type: PDF_MIME,
          period: input.period,
        });
      } catch (err) {
        console.error(
          '[wagepro-web] payslip downloaded, but the bell entry failed',
          err,
        );
      }
    }
  }
  return { ok: true, saved_on_device: uri !== null };
}
